import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { PanelSection, SliderControl } from '@/design-system'
import { ScenePicker } from '@/features/gallery-wizard/components/ScenePicker'
import { FramePicker, FRAME_NONE } from '@/features/gallery-wizard/components/FramePicker'
import { MaterialPicker } from '@/features/gallery-wizard/components/MaterialPicker'
import { ColorPickerStep } from '@/features/gallery-wizard/components/ColorPickerStep'
import { getWizardColors } from '@/features/gallery-wizard/catalog'
import { PatternSelections, Topic } from '@/types'
import { useGallerySourceAlbum } from '../../hooks/useGallerySourceAlbum'
import { StudioCategory, STUDIO_CATEGORIES } from '../../types'
import { StudioAlbumPrintPicker } from './StudioAlbumPrintPicker'

export interface StudioPropertyPanelProps {
  category: StudioCategory
  topic: Topic | null
  selectedAssets: PatternSelections
  onSelect: (category: StudioCategory, value: any) => void
  onMattingWidthChange: (width: number) => void
}

const Root = styled(Box)({
  width: 340,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '1px solid var(--ds-border)',
  backgroundColor: 'var(--ds-surface)',
  overflowY: 'auto',
})

const Header = styled(Box)({
  padding: 'var(--ds-space-4) var(--ds-space-4) 0',
})

const Body = styled(Box)({
  padding: '0 var(--ds-space-4) var(--ds-space-4)',
  flex: 1,
})

const Hint = styled(Typography)({
  fontSize: 12,
  color: 'var(--ds-ink-subtle)',
  marginBottom: 'var(--ds-space-3)',
  lineHeight: 1.5,
})

/** Right-hand panel — hosts the active category's picker inside a PanelSection */
export function StudioPropertyPanel({
  category,
  topic,
  selectedAssets,
  onSelect,
  onMattingWidthChange,
}: StudioPropertyPanelProps) {
  const def = STUDIO_CATEGORIES.find((c) => c.id === category)
  const sourceAlbum = useGallerySourceAlbum(topic)

  return (
    <Root>
      <Header>
        <Typography
          sx={{
            fontFamily: 'var(--ds-font-display)',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.02em',
          }}
        >
          {def?.label ?? 'Studio'}
        </Typography>
      </Header>

      <Body>
        <PanelSection title={def?.label ?? ''} defaultOpen>
          {category === 'template' && (
            <ScenePicker value={selectedAssets.template} onChange={(id) => onSelect('template', id)} />
          )}

          {category === 'material' && (
            <MaterialPicker value={selectedAssets.material} onChange={(id) => onSelect('material', id)} />
          )}

          {category === 'color' && (
            <ColorPickerStep
              value={colorIdFromHex(selectedAssets.color as unknown as string)}
              onChange={(id) => onSelect('color', applyHexForColorId(id))}
            />
          )}

          {category === 'frame' && (
            <FramePicker
              value={selectedAssets.frame ?? FRAME_NONE}
              onChange={(id) => onSelect('frame', id === FRAME_NONE ? undefined : id)}
            />
          )}

          {category === 'matting' && (
            <Box>
              <Hint>Mat width between the frame and the photo. Best with a frame selected.</Hint>
              <SliderControl
                label='Mat width'
                value={selectedAssets.matting?.width ?? 0}
                min={0}
                max={25}
                step={1}
                onChange={onMattingWidthChange}
                valueFormatter={(v) => `${Math.round(v)}%`}
              />
            </Box>
          )}

          {category === 'print' && (
            <Box>
              <Hint>
                Pick a photo from {sourceAlbum.album?.title ? `“${sourceAlbum.album.title}”` : 'the album'}
                , then click a wall in the 3D view to place it there.
              </Hint>
              <StudioAlbumPrintPicker
                albumTitle={sourceAlbum.album?.title}
                photos={sourceAlbum.photos}
                loading={sourceAlbum.loading}
                selectedValue={selectedAssets.print}
                onSelect={(printValue) => onSelect('print', printValue)}
              />
            </Box>
          )}
        </PanelSection>
      </Body>
    </Root>
  )
}

/**
 * The live renderer stores `selectedAssets.color` as a hex string (see
 * ModernAssetSelector's `onSelectAsset(asset.hex[0])`) and its interactive
 * "pick a color, then click a wall" apply path matches it against each
 * swatch's `hex[0]` (`Assets.colors.find(i => i.hex[0] === selection.color)`
 * in renderer/main.js) — NOT the nicer-looking `hex[1]` catalog tone the
 * wizard's ColorPickerStep displays. Bridge both directions through
 * `applyHex` so the grid highlights correctly and, critically, so a real
 * swatch is always found (an unmatched hex leaves the renderer's
 * `currentItem` null while still arming apply-mode, which throws on the
 * next canvas click).
 */
function colorIdFromHex(hex?: string): string | undefined {
  if (!hex) return undefined
  const clean = hex.replace('-custom', '').toLowerCase()
  return getWizardColors().find((c) => c.applyHex.toLowerCase() === clean)?.id
}

function applyHexForColorId(id: string): string | undefined {
  return getWizardColors().find((c) => c.id === id)?.applyHex
}
